import { BaseEdge, EdgeLabelRenderer, getSmoothStepPath } from '@xyflow/react';
import { useStore } from './store';
import { useChatStore } from './chatStore';
import { Plus, Trash2 } from 'lucide-react';
import { useState, useMemo, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

export default function CustomEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  source,
  target,
  selected
}) {
  const insertNodeOnEdge = useStore((s) => s.insertNodeOnEdge);
  const edges = useStore((s) => s.edges);
  const setEdges = useStore((s) => s.setEdges);
  const takeSnapshot = useStore((s) => s.takeSnapshot);

  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef(null);
  const menuPortalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) &&
          menuPortalRef.current && !menuPortalRef.current.contains(event.target)) {
        setShowMenu(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [showMenu]);

  const status = useChatStore((s) => s.status);
  const visitedNodeIds = useChatStore((s) => s.visitedNodeIds);

  const isActive = useMemo(() => {
    if (status === 'idle') return false;
    return visitedNodeIds.includes(source) && visitedNodeIds.includes(target);
  }, [visitedNodeIds, source, target, status]);

  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const onInsert = (type) => {
    insertNodeOnEdge(id, type);
    setShowMenu(false);
  };

  const onDelete = (e) => {
    e.stopPropagation();
    takeSnapshot();
    setEdges(edges.filter((edge) => edge.id !== id));
  };

  const sourceColor = '#92ace6ff';
  const targetColor = '#92ace6ff';
  const gradientId = `gradient-${id}`;

  return (
    <>
      <style>{`
        @keyframes glowDashOffset {
          from {
            stroke-dashoffset: 24;
          }
          to {
            stroke-dashoffset: 0;
          }
        }
      `}</style>
      <defs>
        {/* Dynamic Linear Gradient mapping half Source and half Target colors */}
        <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor={sourceColor} />
          <stop offset="50%" stopColor={sourceColor} />
          <stop offset="50%" stopColor={targetColor} />
          <stop offset="100%" stopColor={targetColor} />
        </linearGradient>
      </defs>

      {/* 🌟 GLOW PATH: Rendered ONLY when path is executing (active) */}
      {isActive && (
        <path
          d={edgePath}
          fill="none"
          stroke={`url(#${gradientId})`} 
          strokeWidth={10}
          style={{
            opacity: 0.6,
            filter: 'blur(3px)',
            pointerEvents: 'none', 
            transition: 'all 0.3s ease',
            strokeDasharray: 'none'
          }}
        />
      )}

      {/* CORE CONNECTOR PATH: Glow colors when executing, simple gray fallback when not executing */}
      <BaseEdge 
        path={edgePath} 
        markerEnd={markerEnd} 
        style={{
          stroke: isActive ? `url(#${gradientId})` : '#e2e8f0',
          strokeWidth: isActive ? 3 : 2,
          strokeLinecap: 'round',
          transition: 'stroke 0.3s ease, strokeWidth 0.3s ease',
          strokeDasharray: 'none'
        }} 
      />

      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
            fontSize: 12,
            pointerEvents: 'all',
          }}
          className="nodrag nopan"
          ref={menuRef}
        >
          <div className={`flex items-center bg-white border border-slate-200 shadow-sm rounded-full ${selected ? 'p-0.5 gap-0.5' : 'p-0'}`}>
            <button
              className={`flex items-center justify-center bg-white rounded-full text-slate-500 hover:text-indigo-500 hover:bg-slate-50 transition-all active:scale-90 ${selected ? 'w-5 h-5' : 'w-6 h-6'}`}
              onClick={() => setShowMenu(!showMenu)}
              title="Insert Node"
            >
              <Plus size={selected ? 11 : 13} />
            </button>
            {selected && (
              <>
                <div className="w-[1px] h-3 bg-slate-200" />
                <button
                  className="w-5 h-5 flex items-center justify-center bg-white rounded-full text-slate-400 hover:text-red-500 hover:bg-red-50 transition-all active:scale-90"
                  onClick={onDelete}
                  title="Delete Connection"
                >
                  <Trash2 size={11} />
                </button>
              </>
            )}
          </div>
        </div>
      </EdgeLabelRenderer>
      
      {showMenu && menuRef.current && createPortal(
        <div 
          ref={menuPortalRef}
          className="fixed bg-white border border-slate-200 rounded-xl shadow-2xl p-2 z-[9999] grid grid-cols-2 gap-1 w-40"
          style={{
            left: menuRef.current.getBoundingClientRect().left + 24,
            top: menuRef.current.getBoundingClientRect().bottom + 8,
          }}
        >
          {['message', 'condition', 'input', 'action', 'delay', 'goto'].map((type) => (
            <button
              key={type}
              onClick={() => onInsert(type)}
              className="px-2 py-1.5 text-[10px] font-bold text-left hover:bg-slate-50 rounded-lg capitalize text-slate-600 hover:text-indigo-500"
            >
              {type}
            </button>
          ))}
        </div>,
        document.body
      )}
    </>
  );
}
